import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getUsers, updateUser, resetUserPassword, deleteUser } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const Users = () => {
  usePageTitle('Users');

  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'accountant',
    is_active: true,
    vendors_view: true,
    vendors_edit: false,
    invoices_view: true,
    invoices_edit: false,
    invoices_approve: false,
    invoices_delete: false,
    projects_view: false,
    projects_edit: false,
    companies_view: false,
    companies_edit: false,
    employees_view: false,
    employees_edit: false,
    users_manage: false,
  });

  const [newPassword, setNewPassword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        full_name: user.full_name,
        role: user.role,
        is_active: Boolean(user.is_active),
        vendors_view: Boolean(user.vendors_view),
        vendors_edit: Boolean(user.vendors_edit),
        invoices_view: Boolean(user.invoices_view),
        invoices_edit: Boolean(user.invoices_edit),
        invoices_approve: Boolean(user.invoices_approve),
        invoices_delete: Boolean(user.invoices_delete),
        projects_view: Boolean(user.projects_view),
        projects_edit: Boolean(user.projects_edit),
        companies_view: Boolean(user.companies_view),
        companies_edit: Boolean(user.companies_edit),
        employees_view: Boolean(user.employees_view),
        employees_edit: Boolean(user.employees_edit),
        users_manage: Boolean(user.users_manage),
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const handleRoleChange = (role) => {
    let newPermissions = { ...formData, role };

    // Set default permissions based on role
    if (role === 'admin') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: true,
        invoices_delete: true,
        projects_view: true,
        projects_edit: true,
        companies_view: true,
        companies_edit: true,
        employees_view: true,
        employees_edit: true,
        users_manage: true,
      };
    } else if (role === 'hr') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: false,
        invoices_view: false,
        invoices_edit: false,
        invoices_approve: false,
        invoices_delete: false,
        projects_view: false,
        projects_edit: false,
        companies_view: false,
        companies_edit: false,
        employees_view: true,
        employees_edit: true,
        users_manage: false,
      };
    } else if (role === 'manager') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: true,
        invoices_delete: true,
        projects_view: true,
        projects_edit: true,
        companies_view: true,
        companies_edit: false,
        employees_view: true,
        employees_edit: true,
        users_manage: false,
      };
    } else {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: false,
        invoices_delete: false,
        projects_view: false,
        projects_edit: false,
        companies_view: true,
        companies_edit: false,
        employees_view: false,
        employees_edit: false,
        users_manage: false,
      };
    }

    setFormData(newPermissions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      // Only update role and permissions, not user details
      const updateData = {
        role: formData.role,
        vendors_view: formData.vendors_view,
        vendors_edit: formData.vendors_edit,
        invoices_view: formData.invoices_view,
        invoices_edit: formData.invoices_edit,
        invoices_approve: formData.invoices_approve,
        invoices_delete: formData.invoices_delete,
        projects_view: formData.projects_view,
        projects_edit: formData.projects_edit,
        companies_view: formData.companies_view || false,
        companies_edit: formData.companies_edit || false,
        employees_view: formData.employees_view || false,
        employees_edit: formData.employees_edit || false,
        users_manage: formData.users_manage,
      };

      await updateUser(editingUser.id, updateData);
      toast.success('Permissions updated successfully');
      closeModal();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error updating permissions');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = (userId) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await resetUserPassword(selectedUserId, newPassword);
      toast.success('Password reset successfully');
      setPasswordModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error resetting password');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${user.full_name}"?`)) {
      return;
    }

    try {
      await deleteUser(user.id);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting user');
    }
  };

  const PermissionBadge = ({ allowed }) => (
    <span style={{ 
      color: allowed ? 'var(--color-accent-success)' : 'var(--color-text-tertiary)',
      display: 'inline-flex',
      alignItems: 'center'
    }}>
      {allowed ? <CheckIcon style={{ width: 16, height: 16 }} /> : <XMarkIcon style={{ width: 16, height: 16 }} />}
    </span>
  );

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading users...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Manage users and their permissions. Users are created automatically when adding employees.</p>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Vendors</th>
                <th style={{ textAlign: 'center' }}>Invoices</th>
                <th style={{ textAlign: 'center' }}>Approve</th>
                <th style={{ textAlign: 'center' }}>Projects</th>
                <th style={{ textAlign: 'center' }}>Users</th>
                <th>Created</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <strong>{user.full_name}</strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                        {user.username} • {user.email}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      user.role === 'admin' ? 'badge-sent' : 
                      user.role === 'manager' ? 'badge-approved' : 'badge-draft'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-approved' : 'badge-rework'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.vendors_view || user.vendors_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.invoices_view || user.invoices_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.invoices_approve} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.projects_view || user.projects_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.users_manage} />
                  </td>
                  <td>{format(new Date(user.created_at), 'MMM d, yyyy')}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openModal(user)} title="Edit">
                        <PencilIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openPasswordModal(user.id)} title="Reset Password">
                        <KeyIcon style={{ width: 16, height: 16 }} />
                      </button>
                      {user.id !== currentUser.id && (
                        <button 
                          className="btn btn-ghost btn-icon btn-sm" 
                          onClick={() => handleDelete(user)} 
                          title="Delete"
                          style={{ color: 'var(--color-accent-danger)' }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="empty-state">
            <UserGroupIcon className="empty-state-icon" />
            <p className="empty-state-title">No users found</p>
          </div>
        )}
      </div>

      {/* User Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Edit User Permissions"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : 'Update Permissions'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {/* User Details - Read Only */}
          <div style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ marginBottom: 'var(--space-md)', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>User Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.username}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.full_name}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.email}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <div style={{ padding: 'var(--space-sm)' }}>
                  <span className={`badge ${formData.is_active ? 'badge-approved' : 'badge-rework'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span style={{ marginLeft: 'var(--space-sm)', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                    (Managed in Employees)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="form-group" style={{ marginBottom: 'var(--space-lg)' }}>
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <option value="accountant">Accountant</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="hr">HR</option>
            </select>
          </div>

          <div style={{ marginTop: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ marginBottom: 'var(--space-md)', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>Permissions</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Vendors</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.vendors_view} onChange={(e) => setFormData({ ...formData, vendors_view: e.target.checked })} />
                  <span>View Vendors</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.vendors_edit} onChange={(e) => setFormData({ ...formData, vendors_edit: e.target.checked })} />
                  <span>Edit Vendors</span>
                </label>
              </div>

              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Invoices</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.invoices_view} onChange={(e) => setFormData({ ...formData, invoices_view: e.target.checked })} />
                  <span>View Invoices</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.invoices_edit} onChange={(e) => setFormData({ ...formData, invoices_edit: e.target.checked })} />
                  <span>Edit Invoices</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.invoices_approve} onChange={(e) => setFormData({ ...formData, invoices_approve: e.target.checked })} />
                  <span>Approve Invoices</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.invoices_delete} onChange={(e) => setFormData({ ...formData, invoices_delete: e.target.checked })} />
                  <span>Delete Invoices</span>
                </label>
              </div>

              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Projects</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.projects_view} onChange={(e) => setFormData({ ...formData, projects_view: e.target.checked })} />
                  <span>View Projects</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.projects_edit} onChange={(e) => setFormData({ ...formData, projects_edit: e.target.checked })} />
                  <span>Edit Projects</span>
                </label>
              </div>

              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Companies</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.companies_view} onChange={(e) => setFormData({ ...formData, companies_view: e.target.checked })} />
                  <span>View Companies</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.companies_edit} onChange={(e) => setFormData({ ...formData, companies_edit: e.target.checked })} />
                  <span>Edit Companies</span>
                </label>
              </div>

              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Employees</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.employees_view} onChange={(e) => setFormData({ ...formData, employees_view: e.target.checked })} />
                  <span>View Employees</span>
                </label>
                <label className="form-checkbox" style={{ marginTop: 'var(--space-xs)' }}>
                  <input type="checkbox" checked={formData.employees_edit} onChange={(e) => setFormData({ ...formData, employees_edit: e.target.checked })} />
                  <span>Edit Employees</span>
                </label>
              </div>

              <div>
                <h5 style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>Administration</h5>
                <label className="form-checkbox">
                  <input type="checkbox" checked={formData.users_manage} onChange={(e) => setFormData({ ...formData, users_manage: e.target.checked })} />
                  <span>Manage Users</span>
                </label>
              </div>
            </div>
          </div>
        </form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title="Reset Password"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setPasswordModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleResetPassword} disabled={saving}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </>
        }
      >
        <div className="form-group mb-0">
          <label className="form-label">New Password</label>
          <input
            type="password"
            className="form-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min 6 characters)"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Users;
